import * as React from 'react';
import {RequireAtLeastOne} from 'type-fest';
import {
  arrow,
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  size,
  useFloating,
  type ArrowOptions,
  type MiddlewareState,
  type ShiftOptions,
  type Rect,
  type UseFloatingOptions,
} from '@floating-ui/react';
import type {LimitShiftOptions} from '@floating-ui/core';

import {
  useOverlay,
  useViewport,
  useIsWithinSpringModal,
  useIsWithinSpringPopover,
} from '@packages/systems/ui/overlay';
import {SpacingNumber} from '../../components/__SharedTypes';
import {isInJest} from '../../utils';
import {useLegacyOverlayCoordinator} from './useFloatingTree';

export const SPACING_BASE_VALUE = 4;
const DEFAULT_PADDING_VALUE = 2;

type PaddingValue = SpacingNumber | `${number}px`;

type ContainerPadding =
  | PaddingValue
  | RequireAtLeastOne<
      Partial<{
        top: PaddingValue;
        right: PaddingValue;
        bottom: PaddingValue;
        left: PaddingValue;
      }>
    >;

export type UseFloatingBaseProps = {
  arrowRef?: ArrowOptions['element'];
  containerPadding?: ContainerPadding;
  defaultIsOpen?: boolean;
  isDisabled?: boolean;
  isOpen?: UseFloatingOptions['open'];
  limitShiftOptions?: LimitShiftOptions;
  maxHeight?: React.CSSProperties['maxHeight'];
  maxWidth?: React.CSSProperties['maxWidth'];
  minWidth?: React.CSSProperties['minWidth'] | 'available';
  offsetModifier?: (options: MiddlewareState) => {
    verticalOffset?: number;
    horizontalOffset?: number;
  } | null;
  onOpenChange?: UseFloatingOptions['onOpenChange'];
  placement?: UseFloatingOptions['placement'];
  shouldEscapeViewport?: boolean;
  shouldMatchTriggerWidth?: boolean;
};

const parseWidthProp = (
  width: React.CSSProperties['maxWidth'] | React.CSSProperties['minWidth']
) =>
  typeof width === 'string' && width.endsWith('px')
    ? parseInt(width, 10)
    : width;

const calculateMaxWidth = ({
  maxWidthProp,
  availableWidth,
  referenceWidth,
  floatingWidth,
  shouldMatchTriggerWidth,
  shouldEscapeViewport,
}: {
  maxWidthProp?: UseFloatingBaseProps['maxWidth'];
  availableWidth: number;
  referenceWidth: number;
  floatingWidth: number;
  shouldMatchTriggerWidth: boolean;
  shouldEscapeViewport: boolean;
}): string => {
  if (shouldMatchTriggerWidth) {
    return `${referenceWidth}px`;
  }

  if (maxWidthProp && shouldEscapeViewport) {
    return typeof maxWidthProp === 'string'
      ? maxWidthProp
      : `${Math.min(availableWidth, maxWidthProp)}px`;
  }

  if (maxWidthProp && typeof maxWidthProp === 'number') {
    return `${Math.min(availableWidth, maxWidthProp, floatingWidth)}px`;
  }

  return `${Math.min(availableWidth, floatingWidth)}px`;
};

const calculateMinWidth = ({
  availableWidth,
  minWidthProp,
  maxWidthProp,
  referenceWidth,
  floatingWidth,
  shouldMatchTriggerWidth,
  shouldEscapeViewport,
}: {
  availableWidth: number;
  minWidthProp?: UseFloatingBaseProps['minWidth'];
  maxWidthProp?: UseFloatingBaseProps['maxWidth'];
  referenceWidth: number;
  floatingWidth: number;
  shouldMatchTriggerWidth: boolean;
  shouldEscapeViewport: boolean;
}) => {
  if (shouldMatchTriggerWidth) {
    return `${referenceWidth}px`;
  }

  if (minWidthProp === 'available') {
    return `${availableWidth}px`;
  }

  if (minWidthProp) {
    return typeof minWidthProp === 'string'
      ? minWidthProp
      : `${minWidthProp}px`;
  }

  /**
   * No minWidth was provided, so we need to make sure that our minimum width doesn't exceed our maxWidth.
   */
  if (maxWidthProp && typeof maxWidthProp === 'number') {
    if (shouldEscapeViewport) {
      return `${Math.min(availableWidth, maxWidthProp, floatingWidth)}px`;
    }
    return `${Math.min(availableWidth, maxWidthProp, floatingWidth)}px`;
  }
  if (!shouldEscapeViewport) {
    return `${Math.min(availableWidth, floatingWidth)}px`;
  }

  // Finally, if no preference is given, we'll default to our available width.
  return 'min-content';
};

const calculateMaxHeight = ({
  maxHeightProp,
  availableHeight,
  elementScrollHeight,
}: {
  maxHeightProp?: UseFloatingBaseProps['maxHeight'];
  availableHeight: number;
  elementScrollHeight: number;
}): string => {
  if (maxHeightProp) {
    return typeof maxHeightProp === 'string'
      ? maxHeightProp
      : `${maxHeightProp}px`;
  }

  /**
   * If the available height is greater than the elements internal height, we don't need to constrain the height.
   *
   * This resolves a bug we noticed in Firefox where the height used for flip is incorrectly calculated based on `max-height` instead of `height`.
   * By setting `maxHeight` to none, we allow the height to be calculated based on the element's internal height.
   *
   * @see: https://github.com/floating-ui/floating-ui/issues/2954
   */
  const fixedAvailableHeight = Math.floor(availableHeight);
  return fixedAvailableHeight > elementScrollHeight
    ? 'none'
    : `${fixedAvailableHeight}px`;
};

const formatPaddingValue = (value: PaddingValue = DEFAULT_PADDING_VALUE) => {
  if (typeof value === 'string') {
    return Number(value.replace('px', ''));
  }

  return value * SPACING_BASE_VALUE;
};

/**
 * A shared base for our FloatingUI components.
 */
export const useFloatingBase = ({
  arrowRef,
  containerPadding = DEFAULT_PADDING_VALUE,
  defaultIsOpen = false,
  isDisabled = false,
  isOpen: controlledIsOpen,
  limitShiftOptions,
  maxHeight: maxHeightProp,
  maxWidth: maxWidthProp,
  minWidth: minWidthProp,
  placement = 'bottom',
  offsetModifier,
  onOpenChange: setControlledOpen,
  shouldEscapeViewport: shouldEscapeViewportProp = false,
  shouldMatchTriggerWidth = false,
}: UseFloatingBaseProps) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] =
    React.useState(defaultIsOpen);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
  const onOpenChange = setControlledOpen ?? setUncontrolledIsOpen;

  const maxWidthPropValue = parseWidthProp(maxWidthProp);
  const minWidthPropValue = parseWidthProp(minWidthProp);

  const legacyCoordinator = useLegacyOverlayCoordinator();

  const isWithinModal = useIsWithinSpringModal();
  const isWithinPopover = useIsWithinSpringPopover();
  const shouldEscapeViewport =
    shouldEscapeViewportProp || isWithinModal || isWithinPopover;
  const overlayContext = useOverlay();
  /**
   * The following type assertion is a temporary workaround to suppress a TypeScript error
   * by assuming Overlay returns a Box with x & y. This is necessary due to differences between
   * Webflow's Box implementation and the HTML Rect object. In the future, a comprehensive
   * migration of Box implementations may resolve this issue.
   */
  const overlayContextDimensions = overlayContext?.[0]?.box as Rect;
  const viewportContextDimensions = useViewport();
  const viewport = shouldEscapeViewport
    ? overlayContextDimensions
    : viewportContextDimensions;

  /**
   * If we aren't in a defined Viewport context, viewport's values will be all 0.
   * In those cases, we want to fall back gracefully to FloatingUI's default behavior (i.e. `clippingAncestors`)
   */
  const boundary =
    viewport?.height === 0 && viewport?.width === 0
      ? 'clippingAncestors'
      : viewport;

  const containerPaddingValue: ShiftOptions['padding'] = React.useMemo(() => {
    if (typeof containerPadding === 'string') {
      return formatPaddingValue(containerPadding);
    }

    if (typeof containerPadding === 'object') {
      return {
        top: formatPaddingValue(containerPadding.top),
        bottom: formatPaddingValue(containerPadding.top),
        left: formatPaddingValue(containerPadding.top),
        right: formatPaddingValue(containerPadding.top),
      };
    }

    return containerPadding * SPACING_BASE_VALUE;
  }, [containerPadding]);

  const middleware = React.useMemo(
    () =>
      isInJest
        ? []
        : [
            offset(
              (options) => {
                /**
                 * If an offsetModifier function has been provided,
                 * the values returned by offsetModifier will take
                 * precedence over the horizontalOffsetProp and verticalOffsetProp.
                 */
                const modifiedOffsetValues =
                  typeof offsetModifier === 'function'
                    ? offsetModifier(options)
                    : undefined;

                const horizontalOffset = modifiedOffsetValues?.horizontalOffset;
                const verticalOffset = modifiedOffsetValues?.verticalOffset;

                const arrowRefElement =
                  arrowRef && 'current' in arrowRef ? arrowRef.current : null;
                const defaultMainAxisOffset = arrowRefElement ? 10 : 4;

                if (placement.includes('top') || placement.includes('bottom')) {
                  return {
                    mainAxis: verticalOffset ?? defaultMainAxisOffset,
                    crossAxis: horizontalOffset ?? 0,
                  };
                } else {
                  return {
                    mainAxis: horizontalOffset ?? defaultMainAxisOffset,
                    crossAxis: verticalOffset ?? 0,
                  };
                }
              },
              [placement]
            ),
            shift(
              {
                boundary,
                padding: containerPaddingValue,
                limiter: limitShift(limitShiftOptions),
              },
              [boundary, containerPaddingValue, limitShiftOptions]
            ),
            flip(
              {
                boundary,
              },
              [boundary]
            ),
            size(
              {
                boundary,
                padding: containerPaddingValue,
                apply({availableWidth, availableHeight, elements, rects}) {
                  const maxWidth = calculateMaxWidth({
                    maxWidthProp: maxWidthPropValue,
                    availableWidth,
                    referenceWidth: rects.reference.width,
                    floatingWidth: rects.floating.width,
                    shouldMatchTriggerWidth,
                    shouldEscapeViewport,
                  });

                  const minWidth = calculateMinWidth({
                    availableWidth,
                    maxWidthProp: maxWidthPropValue,
                    minWidthProp: minWidthPropValue,
                    referenceWidth: rects.reference.width,
                    floatingWidth: rects.floating.width,
                    shouldMatchTriggerWidth,
                    shouldEscapeViewport,
                  });

                  const maxHeight = calculateMaxHeight({
                    maxHeightProp,
                    availableHeight,
                    elementScrollHeight: elements.floating.scrollHeight,
                  });

                  Object.assign(elements.floating.style, {
                    maxWidth,
                    minWidth,
                    maxHeight,
                  });
                },
              },
              [
                boundary,
                containerPaddingValue,
                maxHeightProp,
                maxWidthPropValue,
                minWidthPropValue,
                shouldMatchTriggerWidth,
                shouldEscapeViewport,
              ]
            ),
            arrowRef
              ? arrow({
                  element: arrowRef,
                  padding: 4,
                })
              : null,
          ],
    [
      offsetModifier,
      placement,
      arrowRef,
      boundary,
      containerPaddingValue,
      limitShiftOptions,
      maxHeightProp,
      maxWidthPropValue,
      minWidthPropValue,
      shouldMatchTriggerWidth,
      shouldEscapeViewport,
    ]
  );

  const whileElementsMounted = React.useMemo<typeof autoUpdate | undefined>(
    () =>
      isInJest
        ? undefined
        : (referenceElement, floatingElement, update) => {
            const cleanup = autoUpdate(
              referenceElement,
              floatingElement,
              update,
              {animationFrame: true}
            );
            /*
             * Always return the cleanup function to remove event listeners when tooltip is unmounted.
             */
            return cleanup;
          },
    [isInJest]
  );

  const data = useFloating({
    nodeId: legacyCoordinator.nodeId,
    placement,
    open: isOpen && !isDisabled,
    onOpenChange,
    middleware,
    whileElementsMounted,
  });

  return React.useMemo(
    () => ({...data, ...legacyCoordinator, onOpenChange}),
    [data, legacyCoordinator, onOpenChange]
  );
};
